import Option from '@/components/form/types/Option.ts';
import { formatPrice } from '@/utils/formatters.ts';
import {
  BillLineRef,
  BillVariant,
  BillCatalog,
  catalogVariantsOf,
  variantPriceForAge,
} from '@/features/dashboard/bookingChanges/types/BookingBill.ts';
import {
  CatalogRefDto,
  CatalogRefKind,
} from '@/features/dashboard/availability/types/AvailabilityDto.ts';
import { ChangeItemType } from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import { NO_SELECTION_LABEL } from '@/features/dashboard/bookingChanges/utils/draftItems.ts';
import { BillLineLocation } from './changeItemDraft';
import { NO_SELECTION_OPTION_VALUE } from './editFieldCopy';

/**
 * Lista wartości, które wolno wybrać w edytowanym wierszu.
 *
 * Katalog jest pogrupowany tak, jak pyta o niego wiersz (`catalogVariantsOf`),
 * więc warsztaty pokazują warianty warsztatów, transport — warianty transportu,
 * a pole listy rozwijanej — własne opcje z definicji pola. Wiersz spoza tej
 * czwórki listy nie ma i dostaje kontrolkę właściwą swojemu typowi.
 */

/**
 * Czy wiersz wolno zostawić pusty. Opcja i transport — zawsze; ubezpieczenie
 * pyta o to ofertę wyjazdu, bo `InsuranceChangeOp.CLEAR` przechodzi wyłącznie
 * tam, gdzie wymóg nie obowiązuje.
 */
const isClearable = (ref: BillLineRef, catalog: BillCatalog): boolean => {
  switch (ref.kind) {
    case ChangeItemType.OPTION:
    case ChangeItemType.TRANSPORT:
      return true;
    case ChangeItemType.INSURANCE:
      return !catalog.insuranceRequired;
    default:
      return false;
  }
};

const variantLabel = (variant: BillVariant, age: number | null): string => {
  const price = variantPriceForAge(variant, age);

  return price
    ? `${variant.name} · ${formatPrice(price.amount, price.currency)}`
    : variant.name;
};

/** Wartości bieżące wiersza — jedyne wycofane, które wciąż wolno pokazać. */
const currentIdsOf = (ref: BillLineRef): string[] =>
  ref.currentVariantIds ?? (ref.currentVariantId ? [ref.currentVariantId] : []);

/**
 * Warianty wycofane z oferty nie wchodzą do wyboru, ale ten, który uczestnik ma,
 * zostaje na liście — inaczej modal pokazywałby pustą wartość bieżącą i sam
 * namawiał do zmiany, której nikt nie chciał.
 */
export const resolveFieldOptions = (
  { line }: BillLineLocation,
  catalog: BillCatalog,
  participantAge: number | null,
): Option[] => {
  const { ref } = line;
  if (!ref) {
    return [];
  }

  const current = currentIdsOf(ref);
  const variants = catalogVariantsOf(catalog, ref)
    .filter((variant) => !variant.archived || current.includes(variant.id))
    .map((variant) => ({
      value: variant.id,
      label: variantLabel(variant, participantAge),
    }));

  // Wiersz wielowartościowy czyści się odznaczeniem wszystkiego, więc osobna
  // pozycja „Nie wybrano” byłaby na liście wyborem samym w sobie.
  const clearable = isClearable(ref, catalog) && ref.currentVariantIds === null;

  return clearable
    ? [
        { value: NO_SELECTION_OPTION_VALUE, label: NO_SELECTION_LABEL },
        ...variants,
      ]
    : variants;
};

/**
 * Warianty, o których obłożenie warto zapytać przy tym wierszu.
 *
 * Tylko opcje dodatkowe: transportu pilnuje własny licznik miejsc przy samej
 * podmianie selekcji, a ubezpieczenie limitu w domenie nie ma.
 *
 * Wariant, który uczestnik **już ma**, wypada z pytania: jego miejsce jest
 * policzone po jego stronie, więc komplet chętnych nie jest dla niego żadną
 * przeszkodą — ostrzeżenie przy własnym wyborze byłoby fałszywym alarmem.
 */
export const seatRefsOf = (
  { line }: BillLineLocation,
  catalog: BillCatalog,
): CatalogRefDto[] => {
  const { ref } = line;
  if (!ref || ref.kind !== ChangeItemType.OPTION) {
    return [];
  }
  const held = currentIdsOf(ref);
  return catalogVariantsOf(catalog, ref)
    .filter((variant) => !held.includes(variant.id))
    .map((variant) => ({ kind: CatalogRefKind.VARIANT, id: variant.id }));
};
